// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import loadBillTo from "@insite/client-framework/Store/Data/BillTos/Handlers/LoadBillTo";
import { getCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import loadOrderByOrderNumber from "@insite/client-framework/Store/Data/Orders/Handlers/LoadOrderByOrderNumber";
import { getOrderState } from "@insite/client-framework/Store/Data/Orders/OrdersSelectors";
import loadShipTo from "@insite/client-framework/Store/Data/ShipTos/Handlers/LoadShipTo";
import { getShipToState } from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";
import { getHomePageUrl } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { CreditCardBillingAddressDto } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import LocalizedDateTime from "@insite/content-library/Components/LocalizedDateTime";
import { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import { OrderConfirmationBillingInformationStyles } from "@insite/content-library/Widgets/OrderConfirmation/OrderConfirmationBillingInformation";
import { OrderConfirmationShippingInformationStyles } from "@insite/content-library/Widgets/OrderConfirmation/OrderConfirmationShippingInformation";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import CheckoutSectionHeading, {
    CheckoutSectionHeadingStyles,
} from "@keller/App/Components/Checkout/CheckoutSectionHeading";
import { xIsPickup } from "@keller/App/Store/Selectors/GeneralSelectors";
import { StepperContext } from "@keller/App/Widgets/CheckoutStepper/StepperContext";
import BlockCard, { BlockCardStyles } from "@keller/App/Widgets/ContentBlock/BlockCard";
import BillingAndShippingInfo from "@keller/Overrides/Widgets/CheckoutReviewAndSubmit/BillingAndShippingInfo";
import { paymentMethodValueDisplay } from "@keller/Overrides/Widgets/CheckoutReviewAndSubmit/checkoutReviewAndSubmit.helper";
import { checkoutReviewAndSubmitStyle } from "@keller/Overrides/Widgets/CheckoutReviewAndSubmit/checkoutReviewAndSubmit.shared.styles";
import PickUpLocation from "@keller/Overrides/Widgets/CheckoutReviewAndSubmit/PickUpLocation";
import { EMPTY_VALUE } from "@keller/XDL/constants";
import { ShippingAddressInfo } from "@keller/XDL/types";
import { doNothing, mergeWithCss } from "@keller/XDL/Utilities";
import React, { useContext, useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => {
    const cartState = getCartState(state, state.pages.orderConfirmation.cartId);
    // <XNG-Changes>
    const { fulfillmentMethod, pickUpWarehouse, language } = state.context.session;
    return {
        cartState,
        billToState: getBillToState(state, cartState.value?.billToId),
        shipToState: getShipToState(state, cartState.value?.shipToId),
        orderState: getOrderState(state, cartState.value?.orderNumber),
        pickUpWarehouse,
        enableVat: getSettingsCollection(state).productSettings.enableVat,
        language,
        // XNG-Changes
        editedCartNotes: state.pages.checkoutShipping.editedCartNotes,
        requestedDeliveryDate: state.pages.checkoutReviewAndSubmit.requestedDeliveryDate,
        requestedPickupDate: state.pages.checkoutReviewAndSubmit.requestedPickupDate,
        homePageUrl: getHomePageUrl(state),
        fulfillmentMethod,
    };
};

const mapDispatchToProps = {
    loadBillTo,
    loadShipTo,
    loadOrderByOrderNumber,
};

export interface OrderConfirmationOrderInformationStyles {
    OrderInformationGridContainer?: GridContainerProps;
    orderNumberGridItem?: GridItemProps;
    orderNumberHeading?: TypographyProps;
    orderNumberText?: TypographyPresentationProps;
    shippingInformationGridItem?: GridItemProps;
    vatNumberGridItem?: GridItemProps;
    vatNumberTitleText?: TypographyPresentationProps;
    vatNumberText?: TypographyPresentationProps;
    orderConfirmationShippingInformation?: OrderConfirmationShippingInformationStyles;
    billingInformationGridItem?: GridItemProps;
    orderConfirmationBillingInformation?: OrderConfirmationBillingInformationStyles;
    notesGridItem?: GridItemProps;
    notesTitle: TypographyProps;
    notesDescription?: TypographyPresentationProps;
    orderDetailsGridContainer?: GridContainerProps;
    orderInformationGridItems?: GridItemProps;
    informationGridContainer?: GridContainerProps;
    statusGridItem?: GridItemProps;
    orderDateGridItem?: GridItemProps;
    poNumberGridItem?: GridItemProps;
    salespersonGridItem?: GridItemProps;
    dueDateGridItem?: GridItemProps;
    billingAddressGridItem?: GridItemProps;
    shippingAddressGridItem?: GridItemProps;
    orderNotesGridItem?: GridItemProps;
    headingAndTextStyles?: SmallHeadingAndTextStyles;
    // Xng-Changes:
    blockCard?: BlockCardStyles;
    gridItem?: GridItemProps;
    dividerGridItem?: GridItemProps;
    label?: TypographyProps;
    value?: TypographyProps;
    creditCardBillInformation?: CreditCardBillingAddressDto;
    orderNotesOnPrint?: GridItemProps;
    shippingMethodgridItem?: GridItemProps;
    requestedDeliveryDateGridItem?: GridItemProps;
    doNotShipBeforeDateGridItem?: GridItemProps;
    creditCardGridItem?: GridItemProps;
    purchaseOrderGridItem?: GridItemProps;
    releaseNumberGridItem?: GridItemProps;
    labelName?: CheckoutSectionHeadingStyles;
    orderNumberDisplayGridItem?: GridItemProps;
    orderNumberDisplayGridItemOnPrint?: GridItemProps;
    orderNumberTitleText?: TypographyProps;
    pickUpDateGridItem?: GridItemProps;
}

export const orderInformationStyles: OrderConfirmationOrderInformationStyles = {
    OrderInformationGridContainer: {
        css: css`
            display: flex;
            flex-direction: column;
        `,
    },
    orderNumberGridItem: {
        css: css`
            margin-bottom: 24px;
        `,
    },
    orderNumberHeading: {
        variant: "h2",
        as: "h1",
        css: css`
            margin-bottom: 5px;
        `,
    },
    shippingInformationGridItem: {
        width: 12,
    },
    vatNumberGridItem: {
        width: 12,
        css: css`
            flex-direction: column;
        `,
    },
    vatNumberTitleText: {
        variant: "h6",
        css: css`
            margin-bottom: 5px;
        `,
    },
    billingInformationGridItem: {
        width: 12,
    },
    notesGridItem: {
        width: 12,
        css: css`
            flex-direction: column;
        `,
    },
    notesTitle: {
        variant: "h6",
        as: "h2",
        css: css`
            margin-bottom: 5px;
        `,
    },
    headingAndTextStyles: {
        heading: {
            variant: "h6",
            as: "h2",
            css: css`
                @media print {
                    font-size: 12px;
                }
                margin-bottom: 5px;
            `,
        },
    },
    orderInformationGridItems: {
        css: css`
            @media print {
                padding: 5px !important;
            }
        `,
    },
    statusGridItem: {
        width: [6, 6, 6, 3, 3],
    },
    orderDateGridItem: {
        width: [6, 6, 6, 3, 3],
    },
    poNumberGridItem: {
        width: [6, 6, 6, 3, 3],
    },
    salespersonGridItem: {
        width: [6, 6, 6, 3, 3],
    },
    dueDateGridItem: {
        width: [6, 6, 6, 3, 3],
    },
    billingAddressGridItem: {
        width: 6,
    },
    shippingAddressGridItem: {
        width: 6,
    },
    orderNotesGridItem: {
        width: 12,
    },
    orderDetailsGridContainer: {
        gap: 16,
    },
    blockCard: {
        wrapper: {
            css: css`
                &&& {
                    padding-right: 0;
                    padding-left: 0;
                }
            `,
        },
        infoWrapper: {
            css: css`
                max-width: 100%;
                padding: 24px;
                @media print {
                    padding: 0;
                }
            `,
        },
        title: {
            as: "h3",
            weight: 900,
            css: css`
                margin-bottom: 12px;
                font-size: 24px;
                line-height: 30px;
            `,
        },
        description: {
            weight: "normal",
            css: css`
                margin-bottom: 20px;
            `,
        },
    },
    gridItem: {
        ...checkoutReviewAndSubmitStyle.reviewSection.gridItem,
    },
    dividerGridItem: {
        css: css`
            ${checkoutReviewAndSubmitStyle.reviewSection.dividerGridItem?.css}
            &&& {
                margin-bottom: 25px;
                width: 100%;
                @media print {
                    margin-bottom: 17px;
                    margin-top: 17px;
                }
            }
        `,
    },
    label: {
        ...checkoutReviewAndSubmitStyle.reviewSection.label,
    },
    value: {
        ...checkoutReviewAndSubmitStyle.reviewSection.value,
    },
    orderNumberDisplayGridItem: {
        ...mergeWithCss(checkoutReviewAndSubmitStyle.reviewSection.gridItem, {
            css: css`
                &&& {
                    @media print {
                        display: none;
                    }
                }
            `,
        }),
    },
    orderNumberDisplayGridItemOnPrint: {
        css: css`
            display: none;
            &&& {
                @media print {
                    display: block;
                    max-width: 100%;
                    width: 100%;
                    padding: 0;
                    margin-bottom: 24px;
                }
            }
        `,
    },
    orderNumberTitleText: {
        variant: "h2",
        as: "h1",
        css: css`
            @media print {
                font-size: 28px !important;
            }
        `,
    },
};

// Xng-Changes: use history
type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasHistory;

const styles = orderInformationStyles;

const OrderConfirmationOrderInformation = ({
    cartState,
    billToState,
    shipToState,
    orderState,
    pickUpWarehouse,
    enableVat,
    language,
    loadBillTo,
    loadShipTo,
    loadOrderByOrderNumber,
    editedCartNotes,
    requestedDeliveryDate,
    requestedPickupDate,
    history,
    homePageUrl,
    fulfillmentMethod,
}: Props) => {
    // <XNG-Changes>
    const { checkoutInfoState } = useContext(StepperContext);
    useEffect(() => {
        if (cartState.isLoading || !cartState.value) {
            return;
        }

        const cart = cartState.value;
        if (!billToState.isLoading && !billToState.value && cart.billToId) {
            loadBillTo({ billToId: cart.billToId });
        }

        if (!shipToState.isLoading && !shipToState.value && cart.billToId && cart.shipToId) {
            loadShipTo({ billToId: cart.billToId, shipToId: cart.shipToId });
        }
        if (!orderState.isLoading && !orderState.value && cart.orderNumber && cart.status === "Submitted") {
            loadOrderByOrderNumber({ orderNumber: cart.orderNumber });
        }
    }, [cartState]);

    const cart = cartState.value;
    const billTo = billToState.value;
    const shipTo = shipToState.value;
    const order = orderState.value;
    if (!cart || !billTo || !shipTo) {
        return null;
    }

    // <XNG-Changes>
    const { carrier, shipVia, requestedDeliveryDateDisplay, requestedPickupDateDisplay } = cart;
    // </XNG-Changes>

    // <XNG-Changes>
    const pickUpDate = requestedPickupDate || requestedPickupDateDisplay;
    // </XNG-Changes>

    // <XNG-Changes>
    const isPickup = xIsPickup(cart.fulfillmentMethod);
    const shippingAddressInfo = cart?.properties?.shippingAddressInfo
        ? (JSON.parse(cart.properties.shippingAddressInfo) as ShippingAddressInfo)
        : undefined;
    // </XNG-Changes>
    // Xng-Changes: change structure
    return (
        <StyledWrapper {...styles.OrderInformationGridContainer}>
            <StyledWrapper {...styles.orderNumberGridItem}>
                <BlockCard
                    key="ContentBlock_OrderConfirmation"
                    title={translate("Your Order Has Been Placed.")}
                    description={siteMessage(
                        "Keller_OrderConfirmation_OrderNumber",
                        cart.erpOrderNumber || cart.orderNumber,
                    ).toString()}
                    buttonVariant="primary"
                    buttonLabel={translate("Continue Shopping")}
                    link={{ value: homePageUrl, type: "Url" }}
                    size="medium"
                    extendedStyles={styles.blockCard}
                    buttonSizeVariant="medium"
                />
            </StyledWrapper>
            {/* XNG-Changes: add Order # as heading */}
            <GridItem {...styles.orderNumberDisplayGridItemOnPrint}>
                <Typography {...styles.orderNumberTitleText}>
                    {translate("Order #: ")}
                    {cart.erpOrderNumber || cart.orderNumber || EMPTY_VALUE}
                </Typography>
            </GridItem>
            {/* XNG-Changes: update structure & add General Heading */}
            <GridContainer {...styles.orderDetailsGridContainer}>
                <CheckoutSectionHeading extendedStyles={styles.labelName} label={translate("General")} />
                <GridItem {...styles.orderNumberDisplayGridItem}>
                    <Typography {...styles.label}>{translate("Order #")}</Typography>
                    <Typography {...styles.value}>{cart.erpOrderNumber || cart.orderNumber || EMPTY_VALUE}</Typography>
                </GridItem>
                {/* XNG-Changes */}
                {/* <SmallHeadingAndText
                    extendedStyles={styles.headingAndTextStyles}
                    heading={translate("Order Date")}
                    text={
                        order?.orderDate
                            ? getLocalizedDateTime({
                                  dateTime: new Date(order.orderDate as Date),
                                  language,
                              })
                            : ""
                    }
                /> */}
                {getLocalizedDateTime({
                    dateTime: new Date(order?.orderDate as Date),
                    language,
                }) && (
                    <GridItem {...styles.gridItem}>
                        <Typography {...styles.label}>{translate("Order Date")}</Typography>
                        <Typography {...styles.value}>
                            {order?.orderDate
                                ? getLocalizedDateTime({
                                      dateTime: new Date(order.orderDate as Date),
                                      language,
                                  })
                                : ""}
                        </Typography>
                    </GridItem>
                )}
                {(order?.statusDisplay || cart.statusDisplay) && (
                    <GridItem {...styles.gridItem}>
                        <Typography {...styles.label}>{translate("Status")}</Typography>
                        <Typography {...styles.value}>{order?.statusDisplay || cart.statusDisplay}</Typography>
                    </GridItem>
                )}
                {cart?.salespersonName && (
                    <GridItem {...styles.gridItem}>
                        <Typography {...styles.label}>{translate("Salesperson")}</Typography>
                        <Typography {...styles.value}>{cart.salespersonName}</Typography>
                    </GridItem>
                )}
                {enableVat && cart.customerVatNumber && (
                    <GridItem {...styles.gridItem}>
                        <Typography {...styles.label}>{translate("VAT Number")}</Typography>
                        <Typography {...styles.value}>{cart.customerVatNumber}</Typography>
                    </GridItem>
                )}
                {/* XNG-Changes: add Order By */}
                {cart?.properties?.orderByFullName && (
                    <GridItem {...styles.gridItem}>
                        <Typography {...styles.label}>{translate("Ordered By")}</Typography>
                        <Typography {...styles.value}>{cart.properties.orderByFullName}</Typography>
                    </GridItem>
                )}
                {cart?.properties?.associateName && (
                    <GridItem {...styles.gridItem}>
                        <Typography {...styles.label}>{translate("Person Who Helped You")}</Typography>
                        <Typography {...styles.value}>{cart.properties.associateName}</Typography>
                    </GridItem>
                )}
                {/* XNG-CHANGE: show Shipping Method just in case of Ship and add !isPickup */}
                {!isPickup && carrier?.description && (
                    <GridItem {...styles.shippingMethodgridItem}>
                        <Typography {...styles.label}>{translate("Shipping Method")}</Typography>
                        <Typography {...styles.value}>{carrier?.description}</Typography>
                    </GridItem>
                )}
                {/* XNG-CHANGE */}
                {(requestedDeliveryDate || requestedDeliveryDateDisplay) && (
                    <GridItem {...styles.requestedDeliveryDateGridItem}>
                        <Typography {...styles.label}>{translate("Requested by Date")}</Typography>
                        <Typography {...styles.value} data-test-selector="requestedDeliveryDateValue">
                            <LocalizedDateTime dateTime={requestedDeliveryDate || requestedDeliveryDateDisplay} />
                        </Typography>
                    </GridItem>
                )}
                {/* XNG-CHANGE */}
                {checkoutInfoState.doNotShipBeforeDate && (
                    <GridItem {...styles.doNotShipBeforeDateGridItem}>
                        <Typography {...styles.label}>{translate("Do not shipping before Date")}</Typography>
                        <Typography {...styles.value} data-test-selector="doNotShipBeforeDate">
                            <LocalizedDateTime dateTime={checkoutInfoState.doNotShipBeforeDate} />
                        </Typography>
                    </GridItem>
                )}
                {/* XNG-CHANGE */}
                {isPickup && pickUpDate && (
                    <GridItem {...styles.pickUpDateGridItem}>
                        <Typography {...styles.label}>{translate("Pick Up Date")}</Typography>
                        <Typography {...styles.value} data-test-selector="requestedPickUpDateValue">
                            <LocalizedDateTime dateTime={pickUpDate} />
                        </Typography>
                    </GridItem>
                )}
                {/* XNG-CHANGE */}
                {paymentMethodValueDisplay(checkoutInfoState) && (
                    <GridItem {...styles.creditCardGridItem}>
                        <Typography {...styles.label}>
                            {checkoutInfoState.isCreditCard ? translate("Credit Card") : translate("Payment Method")}
                        </Typography>
                        <Typography {...styles.value}>{paymentMethodValueDisplay(checkoutInfoState)}</Typography>
                    </GridItem>
                )}
                {/* XNG-CHANGE */}
                {checkoutInfoState.poNumber && (
                    <GridItem {...styles.purchaseOrderGridItem}>
                        <Typography {...styles.label}>{translate("Purchase Order")}</Typography>
                        <Typography {...styles.value}>{checkoutInfoState.poNumber}</Typography>
                    </GridItem>
                )}
                {/* XNG-CHANGE */}
                {checkoutInfoState.releaseNumber && (
                    <GridItem {...styles.releaseNumberGridItem}>
                        <Typography {...styles.label}>{translate("Release Number")}</Typography>
                        <Typography {...styles.value}>{checkoutInfoState.releaseNumber}</Typography>
                    </GridItem>
                )}

                {/* XNG-CHANGE */}
                {(editedCartNotes || cart.notes) && (
                    <GridItem {...styles.orderNotesOnPrint}>
                        <Typography {...styles.label}>{translate("Order Notes")}</Typography>
                        <Typography {...styles.value} data-test-selector="orderNotes">
                            {editedCartNotes || cart.notes}
                        </Typography>
                    </GridItem>
                )}
            </GridContainer>
            <StyledWrapper {...styles.dividerGridItem} />
            {/* <XNG-Changes>: use BillingAndShippingInfo and add !isPickup  */}
            {!isPickup && (
                <BillingAndShippingInfo
                    billTo={billTo}
                    shipTo={shipTo}
                    carrier={carrier!}
                    shipVia={shipVia!}
                    // XNG-Changes apply requestedDeliveryDate if exists if not use cart.requestedDeliveryDateDisplay
                    deliveryDate={requestedDeliveryDate || requestedDeliveryDateDisplay}
                    onEditBillTo={doNothing}
                    onEditShipTo={doNothing}
                    // XNG-Changes apply editedCartNotes if exists if not use cart.notes
                    {...{ notes: editedCartNotes || cart.notes, canGoBack: false }}
                    // XNG-Changes pass creditCardBillInformation
                    creditCardBillInformation={cart.creditCardBillingAddress as CreditCardBillingAddressDto}
                />
            )}
            {/* <XNG-Changes> use PickUpLocation and add isPickup */}
            {isPickup && (
                <PickUpLocation
                    location={pickUpWarehouse!}
                    billTo={billTo}
                    // XNG-Changes apply requestedPickupDate if exists if not use cart.requestedPickupDateDisplay
                    pickUpDate={requestedPickupDate || requestedPickupDateDisplay}
                    onEditLocation={doNothing}
                    onEditBillTo={doNothing}
                    canGoBack={false}
                    // XNG-Changes pass creditCardBillInformation
                    creditCardBillInformation={cart.creditCardBillingAddress as CreditCardBillingAddressDto}
                    // XNG-Changes apply editedCartNotes if exists if not use cart.notes
                    {...{ notes: editedCartNotes || cart.notes }}
                    // XNG-Changes
                    shippingAddressInfo={shippingAddressInfo}
                />
            )}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(OrderConfirmationOrderInformation)),
    definition: {
        displayName: "Order Information",
        allowedContexts: ["OrderConfirmationPage"],
        group: "Order Confirmation",
    },
};

export default widgetModule;
